import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, Button, Badge, Reveal, Skeleton, MapPreview, useIsMobile } from '../components/ui';
import PageLayout from '../components/layout/PageLayout';
import { MapPin, Calendar, Phone, Navigation } from 'lucide-react';
import api from '../utils/api';
import { directionsUrl, hasCoords } from '../utils/maps';
import { useAuth } from '../context/AuthContext';

export default function Camps() {
  const { user } = useAuth();
  const [camps, setCamps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [registering, setRegistering] = useState({});
  const [registered, setRegistered] = useState({});
  const [openMap, setOpenMap] = useState({});
  const isMobile = useIsMobile();
  const isDonor = user?.role === 'donor';

  useEffect(() => {
    api.get('/organizations/camps')
      .then(r => setCamps(r.data))
      .catch(() => { setCamps([]); setLoadError(true); })
      .finally(() => setLoading(false));
  }, []);

  // Without this, a donor who registered in an earlier session comes back to a
  // live "Register" button and only learns they're already in when the POST
  // 400s. Registrations live server-side, so seed the map from there.
  useEffect(() => {
    if (!isDonor) { setRegistered({}); return; }
    api.get('/organizations/camps/my/registrations')
      .then(r => setRegistered(Object.fromEntries(r.data.map(reg => [reg.camp_id, true]))))
      .catch(() => {});
  }, [isDonor]);

  const register = async (id) => {
    setRegistering(r => ({ ...r, [id]: true }));
    try {
      await api.post(`/organizations/camps/${id}/register`);
      setRegistered(r => ({ ...r, [id]: true }));
      setCamps(c => c.map(camp => camp.id === id ? { ...camp, slots_booked: camp.slots_booked + 1 } : camp));
    } catch (e) {
      const detail = e.response?.data?.detail || 'Registration failed.';
      // Registered in another tab/session since this list loaded — reflect the
      // real state instead of leaving a button that can only fail again.
      if (/already registered/i.test(detail)) setRegistered(r => ({ ...r, [id]: true }));
      else alert(detail);
    } finally {
      setRegistering(r => ({ ...r, [id]: false }));
    }
  };

  return (
    <PageLayout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '20px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 800, letterSpacing: '-0.03em', color: 'var(--gray-900)' }}>Donation Camps</h1>
          <p style={{ fontSize: '13.5px', color: 'var(--gray-500)', marginTop: '4px' }}>Find a camp near you and book your slot.</p>
        </div>
        {user?.role === 'organization' && (
          <Link to="/org/camps/new" style={{ flexShrink: 0 }}>
            <Button size="sm" variant="primary">+ New Camp</Button>
          </Link>
        )}
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {[0, 1, 2].map(i => (
            <Card key={i}>
              <Skeleton width="60%" height="16px" style={{ marginBottom: '10px' }} />
              <Skeleton width="40%" height="12px" style={{ marginBottom: '18px' }} />
              <Skeleton width="100%" height="6px" style={{ marginBottom: '14px' }} />
              <Skeleton width="30%" height="30px" radius="8px" />
            </Card>
          ))}
        </div>
      ) : loadError ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--gray-400)' }}>
            <div style={{ fontSize: '36px', marginBottom: '10px', opacity: 0.6 }}>⚠️</div>
            <p style={{ fontSize: '14px', fontWeight: 500, color: 'var(--gray-600)' }}>Couldn't load camps right now.</p>
            <p style={{ fontSize: '13px', color: 'var(--gray-400)' }}>Please check your connection and try again.</p>
          </div>
        </Card>
      ) : camps.length === 0 ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--gray-400)' }}>
            <div style={{ fontSize: '36px', marginBottom: '10px', opacity: 0.6 }}>⛺</div>
            <p style={{ fontSize: '14px', fontWeight: 500, color: 'var(--gray-600)' }}>No upcoming camps right now.</p>
            <p style={{ fontSize: '13px', color: 'var(--gray-400)' }}>Check back soon, or register your organization to host one.</p>
          </div>
        </Card>
      ) : camps.map((camp, i) => {
        const pct = Math.round((camp.slots_booked / camp.slots_total) * 100);
        const full = camp.slots_booked >= camp.slots_total;
        const date = new Date(camp.camp_date);
        const slotsLeft = camp.slots_total - camp.slots_booked;
        const barColor = pct > 90 ? 'var(--red)' : pct > 60 ? 'var(--amber)' : 'var(--green)';
        const directions = directionsUrl(camp);

        return (
          <Reveal key={camp.id} delay={Math.min(i, 4) * 60}>
            <Card style={{ marginBottom: '12px' }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div style={{ flex: 1, minWidth: 0, paddingRight: '12px' }}>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--gray-900)', marginBottom: '5px' }}>{camp.title}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12.5px', color: 'var(--gray-500)' }}>
                    <MapPin size={12} color="var(--gray-400)" />
                    {camp.venue_name}, {[camp.city, camp.country].filter(Boolean).join(', ')}
                  </div>
                  {camp.address && (
                    <div style={{ fontSize: '12px', color: 'var(--gray-400)', marginTop: '3px', paddingLeft: '17px' }}>{camp.address}</div>
                  )}
                </div>
                <Badge color={full ? 'red' : 'green'}>{full ? 'Full' : 'Open'}</Badge>
              </div>

              {/* Meta row */}
              <div style={{ display: 'flex', gap: isMobile ? '10px' : '20px', fontSize: '12.5px', color: 'var(--gray-500)', marginBottom: '14px', flexWrap: 'wrap' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <Calendar size={12} color="var(--gray-400)" />
                  {date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                  {' · '}
                  {date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                </span>
                {camp.contact_phone && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Phone size={12} color="var(--gray-400)" />
                    {camp.contact_phone}
                  </span>
                )}
                {directions && (
                  <a
                    href={directions}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 600, color: 'var(--red)' }}
                  >
                    <Navigation size={12} /> Directions
                  </a>
                )}
                {hasCoords(camp) && (
                  <button
                    type="button"
                    onClick={() => setOpenMap(m => ({ ...m, [camp.id]: !m[camp.id] }))}
                    style={{
                      background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                      font: 'inherit', fontWeight: 600, color: 'var(--gray-600)',
                    }}
                  >
                    {openMap[camp.id] ? 'Hide map' : 'Show map'}
                  </button>
                )}
              </div>

              {/* Rendered on demand — the listing can hold 50 camps and eagerly
                  mounting that many map iframes would stall the page. */}
              {openMap[camp.id] && <MapPreview place={camp} style={{ marginBottom: '14px' }} />}

              {/* Slots progress */}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px' }}>
                <span style={{ color: 'var(--gray-500)' }}>Slots filled</span>
                <span style={{ fontWeight: 600, color: pct > 90 ? 'var(--red)' : 'var(--gray-700)' }}>
                  {camp.slots_booked} / {camp.slots_total}
                </span>
              </div>
              <div style={{ height: '6px', borderRadius: '4px', background: 'var(--gray-100)', marginBottom: '14px' }}>
                <div style={{ height: '100%', borderRadius: '4px', width: `${pct}%`, background: barColor, transition: 'width 0.5s ease' }} />
              </div>

              {/* Footer */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12.5px', color: slotsLeft <= 5 ? 'var(--red)' : 'var(--gray-500)', fontWeight: slotsLeft <= 5 ? 600 : 400 }}>
                  {slotsLeft} slot{slotsLeft !== 1 ? 's' : ''} remaining
                </span>
                {isDonor ? (
                  registered[camp.id]
                    ? <Badge color="green">✓ Registered</Badge>
                    : <Button
                        size="sm"
                        variant={full ? 'secondary' : 'primary'}
                        loading={registering[camp.id]}
                        onClick={() => !full && register(camp.id)}
                      >
                        {full ? 'Join Waitlist' : 'Register'}
                      </Button>
                ) : (
                  <Badge color="gray">{camp.status}</Badge>
                )}
              </div>
            </Card>
          </Reveal>
        );
      })}
    </PageLayout>
  );
}
